import React, { createContext, useContext } from "react";
import {
  DefaultFeatureFlagService,
  type FeatureFlagService,
  type FeatureFlags,
  type FeatureFlagKey,
} from "../features";

/**
 * Config object exposed to components via ConfigContext
 */
export interface Config {
  /**
   * All current feature flags
   */
  features: FeatureFlags;

  /**
   * Check if a specific feature is enabled
   */
  isFeatureEnabled(flag: FeatureFlagKey): boolean;

  /**
   * Get shareable URL with current flags
   */
  getShareableUrl(): string;
}

/**
 * Context for accessing application configuration
 */
const ConfigContext = createContext<Config | undefined>(undefined);

/**
 * Props for ConfigProvider
 */
export interface ConfigProviderProps extends React.PropsWithChildren {
  featureFlagService?: FeatureFlagService;
}

/**
 * Provider component that wraps the app with configuration context
 * Should be placed at the root of the app, wrapping all feature-dependent components
 *
 * Usage:
 * ```tsx
 * <ConfigProvider featureFlagService={new FeatureFlagService()}>
 *   <App />
 * </ConfigProvider>
 * ```
 */
export function ConfigProvider({
  children,
  featureFlagService = new DefaultFeatureFlagService(),
}: ConfigProviderProps): JSX.Element {
  const config: Config = {
    features: featureFlagService.getAll(),
    isFeatureEnabled: (flag: FeatureFlagKey) =>
      featureFlagService.isEnabled(flag),
    getShareableUrl: () => featureFlagService.getShareableUrl(),
  };

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
}

/**
 * Hook to access config from any component within ConfigProvider
 * Throws error if used outside ConfigProvider
 *
 * Usage:
 * ```tsx
 * const config = useConfig();
 * if (config.isFeatureEnabled('newStoreGroupServiceEnabled')) {
 *   return <NewStoreGroups />;
 * }
 * return <OldStoreGroups />;
 * ```
 */
export function useConfig(): Config {
  const config = useContext(ConfigContext);
  if (!config) {
    throw new Error(
      "useConfig must be used within a ConfigProvider. Make sure ConfigProvider wraps your component tree.",
    );
  }
  return config;
}
